import React, { useState, useEffect, useRef } from 'react';
import { Title, Loading } from 'react-admin';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  const h = { Accept: 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
};

const pct = (v) => (v != null ? `${(v * 100).toFixed(1)}%` : '—');
const fmt = (v) => (v != null ? (v * 100).toFixed(1) : null);

// ─── Summary card ────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, sub, color }) => (
  <div style={{
    background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
    padding: '18px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    minWidth: 160, flex: 1,
  }}>
    <div style={{ fontSize: '2em', fontWeight: 700, color: color || '#333', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: '0.8em', color: '#666', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    {sub && <div style={{ fontSize: '0.75em', color: '#aaa', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Version badge ────────────────────────────────────────────────────────────
const versionLabel = (v) => {
  if (!v) return 'nieznana';
  // "2026-04-19T11:05:56.009329+00:00" → "19.04.2026 11:05"
  try {
    return new Date(v).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return v.slice(0, 16);
  }
};

// ─── Rolling average chart ────────────────────────────────────────────────────
const ROLL = 20; // window size

const rollingAvg = (data, key, window) => {
  return data.map((_, i) => {
    const from = Math.max(0, i - Math.floor(window / 2));
    const to = Math.min(data.length - 1, i + Math.floor(window / 2));
    const vals = [];
    for (let j = from; j <= to; j++) {
      if (data[j][key] != null) vals.push(data[j][key]);
    }
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  });
};

const SVGLineChart = ({ data, avgPrecision, avgRecall }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  const W = 800, H = 260;
  const PAD = { top: 20, right: 64, bottom: 40, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  if (!data.length) {
    return (
      <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>
        Brak ocenionych predykcji do wyświetlenia
      </div>
    );
  }

  const n = data.length;
  const xScale = (i) => PAD.left + (i / Math.max(n - 1, 1)) * chartW;
  const yScale = (v) => PAD.top + chartH - (v / 100) * chartH;

  const precRoll = rollingAvg(data, 'precision', ROLL);
  const recRoll = rollingAvg(data, 'recall', ROLL);

  const smoothPath = (vals) => {
    const pts = vals.map((v, i) => v != null ? [xScale(i), yScale(v)] : null).filter(Boolean);
    if (pts.length < 2) return null;
    // simple polyline — could use bezier but polyline is fine at this density
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  };

  const yTicks = [0, 25, 50, 75, 100];
  const xTickStep = Math.max(1, Math.ceil(n / 8));

  const refLine = (val, color) => val == null ? null : (
    <g>
      <line
        x1={PAD.left} y1={yScale(val * 100)} x2={PAD.left + chartW} y2={yScale(val * 100)}
        stroke={color} strokeDasharray="5 4" strokeWidth={1} opacity={0.5}
      />
      <text x={PAD.left + chartW + 4} y={yScale(val * 100) + 4} fontSize={9} fill={color} opacity={0.8}>
        {pct(val)}
      </text>
    </g>
  );

  // Invisible hit-test rects for tooltip
  const hitRects = data.map((d, i) => (
    <rect
      key={i}
      x={xScale(i) - chartW / n / 2} y={PAD.top}
      width={Math.max(4, chartW / n)} height={chartH}
      fill="transparent"
      style={{ cursor: 'crosshair' }}
      onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, d, i })}
      onMouseLeave={() => setTooltip(null)}
    />
  ));

  const precPath = smoothPath(precRoll);
  const recPath = smoothPath(recRoll);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 20, marginBottom: 8, fontSize: '0.8em', alignItems: 'center' }}>
        <span><span style={{ display: 'inline-block', width: 24, height: 3, background: '#2e7d32', verticalAlign: 'middle', marginRight: 5, borderRadius: 2 }} />Precyzja</span>
        <span><span style={{ display: 'inline-block', width: 24, height: 3, background: '#1565c0', verticalAlign: 'middle', marginRight: 5, borderRadius: 2 }} />Pokrycie (recall@10)</span>
        <span style={{ color: '#aaa' }}><span style={{ display: 'inline-block', width: 20, height: 0, borderTop: '2px dashed #bbb', verticalAlign: 'middle', marginRight: 5 }} />Średnia ogólna</span>
        <span style={{ color: '#bbb', fontSize: '0.9em' }}>krocząca średnia {ROLL} przetargów</span>
      </div>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* Grid */}
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} y1={yScale(t)} x2={PAD.left + chartW} y2={yScale(t)} stroke={t === 0 ? '#ddd' : '#f2f2f2'} />
            <text x={PAD.left - 6} y={yScale(t) + 4} textAnchor="end" fontSize={10} fill="#bbb">{t}%</text>
          </g>
        ))}
        {/* X ticks */}
        {data.map((d, i) => i % xTickStep === 0 && (
          <text key={i} x={xScale(i)} y={PAD.top + chartH + 16} textAnchor="middle" fontSize={9} fill="#ccc">{i + 1}</text>
        ))}
        {/* Axes */}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="#e0e0e0" />
        <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="#e0e0e0" />
        {/* X label */}
        <text x={PAD.left + chartW / 2} y={H - 4} textAnchor="middle" fontSize={10} fill="#ccc">Przetarg (kolejność oceny)</text>
        {/* Reference lines (overall avg) */}
        {refLine(avgPrecision, '#2e7d32')}
        {refLine(avgRecall, '#1565c0')}
        {/* Raw scatter dots */}
        {data.map((d, i) => (
          <g key={i}>
            {d.precision != null && <circle cx={xScale(i)} cy={yScale(d.precision)} r={2} fill="#2e7d32" opacity={0.18} />}
            {d.recall != null && <circle cx={xScale(i)} cy={yScale(d.recall)} r={2} fill="#1565c0" opacity={0.18} />}
          </g>
        ))}
        {/* Rolling avg paths on top */}
        {precPath && <path d={precPath} fill="none" stroke="#2e7d32" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />}
        {recPath && <path d={recPath} fill="none" stroke="#1565c0" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />}
        {/* Tooltip marker */}
        {tooltip && (
          <g>
            <line x1={xScale(tooltip.i)} y1={PAD.top} x2={xScale(tooltip.i)} y2={PAD.top + chartH} stroke="#ddd" strokeWidth={1} />
            {tooltip.d.precision != null && <circle cx={xScale(tooltip.i)} cy={yScale(precRoll[tooltip.i] ?? tooltip.d.precision)} r={4} fill="#2e7d32" stroke="#fff" strokeWidth={1.5} />}
            {tooltip.d.recall != null && <circle cx={xScale(tooltip.i)} cy={yScale(recRoll[tooltip.i] ?? tooltip.d.recall)} r={4} fill="#1565c0" stroke="#fff" strokeWidth={1.5} />}
          </g>
        )}
        {/* Hit-test rects (on top) */}
        {hitRects}
      </svg>
      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 14, top: tooltip.y - 50, zIndex: 9999,
          background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
          padding: '8px 12px', fontSize: '0.8em', pointerEvents: 'none',
          boxShadow: '0 3px 12px rgba(0,0,0,0.10)', minWidth: 180,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#555' }}>Przetarg #{tooltip.i + 1}</div>
          {tooltip.d.precision != null && (
            <div style={{ color: '#2e7d32' }}>Precyzja: <strong>{tooltip.d.precision}%</strong>
              {precRoll[tooltip.i] != null && <span style={{ color: '#aaa', fontWeight: 400 }}> (śr. {precRoll[tooltip.i].toFixed(1)}%)</span>}
            </div>
          )}
          {tooltip.d.recall != null && (
            <div style={{ color: '#1565c0' }}>Pokrycie: <strong>{tooltip.d.recall}%</strong>
              {recRoll[tooltip.i] != null && <span style={{ color: '#aaa', fontWeight: 400 }}> (śr. {recRoll[tooltip.i].toFixed(1)}%)</span>}
            </div>
          )}
          {tooltip.d.tender_title && (
            <div style={{ color: '#999', marginTop: 4, fontSize: '0.9em' }}>
              {tooltip.d.tender_title.slice(0, 60)}{tooltip.d.tender_title.length > 60 ? '…' : ''}
            </div>
          )}
          {tooltip.d.date && <div style={{ color: '#ccc', fontSize: '0.85em', marginTop: 2 }}>{tooltip.d.date}</div>}
        </div>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const ModelStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  useEffect(() => {
    fetch(`${API_URL}/tenders/model-stats/`, { headers: authHeaders() })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setData(d);
        // default: show all versions
        setSelectedVersion('all');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (error) return <div style={{ padding: 24, color: '#c62828' }}>Błąd: {error}</div>;
  if (!data) return null;

  const { summary, by_model_version, timeline } = data;
  const latestVersion = by_model_version.length ? by_model_version[0] : null;

  // Build chart data filtered by selected version
  const filteredTimeline = timeline.filter(
    (r) => selectedVersion === 'all' || r.model_version === selectedVersion
  );

  const chartData = filteredTimeline.map((r, i) => ({
    idx: i + 1,
    date: r.evaluated_at ? new Date(r.evaluated_at).toLocaleDateString('pl-PL') : '',
    precision: fmt(r.precision),
    recall: fmt(r.recall),
    model_version: r.model_version,
    tender_title: r.tender__title,
    tender_id: r.tender_id,
  }));

  // Compute version-specific averages for reference lines
  const versionAvgPrecision = selectedVersion !== 'all'
    ? by_model_version.find((v) => v.model_version === selectedVersion)?.avg_precision
    : summary.avg_precision;
  const versionAvgRecall = selectedVersion !== 'all'
    ? by_model_version.find((v) => v.model_version === selectedVersion)?.avg_recall
    : summary.avg_recall;

  // Reset page when version filter changes
  const handleVersionChange = (v) => { setSelectedVersion(v); setPage(1); };

  // Color palette for model versions
  const versionColors = ['#c62828', '#1565c0', '#2e7d32', '#6a1b9a', '#e65100', '#00695c'];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <Title title="Skuteczność modelu predykcji" />

      <h2 style={{ marginTop: 0, marginBottom: 4, fontSize: '1.4em', color: '#333' }}>
        Skuteczność modelu predykcji
      </h2>
      <p style={{ color: '#888', marginBottom: 24, fontSize: '0.85em' }}>
        Łącznie wygenerowano {summary.total_runs} predykcji · {summary.total_evaluated} ocenionych · {summary.pending_count} czeka na ocenę.
      </p>

      {/* ── Per-version table ── */}
      <div style={{
        background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
        padding: '20px 24px', marginBottom: selectedVersion !== 'all' ? 0 : 32,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        borderBottomLeftRadius: selectedVersion !== 'all' ? 0 : 10,
        borderBottomRightRadius: selectedVersion !== 'all' ? 0 : 10,
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1em', color: '#333' }}>Porównanie wersji modelu</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875em' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              {['', 'Wersja modelu', 'Ocenione / wygenerowane', 'Śr. precyzja', 'Śr. pokrycie (recall@10)', 'Okres'].map((h, i) => (
                <th key={i} style={{ textAlign: i < 2 ? 'left' : 'center', padding: '6px 10px', color: '#888', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {by_model_version.map((v, i) => {
              const color = versionColors[i % versionColors.length];
              const isSelected = selectedVersion === v.model_version;
              return (
                <tr
                  key={v.model_version || i}
                  onClick={() => handleVersionChange(isSelected ? 'all' : v.model_version)}
                  style={{
                    borderBottom: '1px solid #f0f0f0',
                    background: isSelected ? '#fff8f8' : 'transparent',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: color }} />
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 600, color }}>
                    {versionLabel(v.model_version)}
                    {i === 0 && <span style={{ marginLeft: 8, background: '#e8f5e9', color: '#2e7d32', borderRadius: 8, padding: '1px 7px', fontSize: '0.72em', fontWeight: 600 }}>aktywny</span>}
                    {isSelected && <span style={{ marginLeft: 6, color: '#c62828', fontSize: '0.8em' }}>◀</span>}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 10px' }}>
                    <span style={{ fontWeight: 700 }}>{v.evaluated_count}</span>
                    <span style={{ color: '#aaa', fontSize: '0.88em' }}> / {v.total_count ?? '—'}</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 10px' }}>
                    <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 12, padding: '2px 10px', fontWeight: 700 }}>
                      {pct(v.avg_precision)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 10px' }}>
                    <span style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: 12, padding: '2px 10px', fontWeight: 700 }}>
                      {pct(v.avg_recall)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 10px', color: '#888', fontSize: '0.82em' }}>
                    {v.first_run ? new Date(v.first_run).toLocaleDateString('pl-PL') : '—'}
                    {' – '}
                    {v.last_run ? new Date(v.last_run).toLocaleDateString('pl-PL') : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p style={{ fontSize: '0.75em', color: '#aaa', marginTop: 8, marginBottom: 0 }}>
          Kliknij wiersz aby zobaczyć szczegóły i przefiltrować wykres.
        </p>
      </div>

      {/* ── Detail panel for selected version ── */}
      {selectedVersion !== 'all' && (() => {
        const v = by_model_version.find((x) => x.model_version === selectedVersion);
        if (!v) return null;
        const color = versionColors[by_model_version.indexOf(v) % versionColors.length];
        const pending = (v.total_count ?? v.evaluated_count) - v.evaluated_count;
        return (
          <div style={{
            background: '#fafafa', border: '1px solid #e8e8e8', borderTop: '2px solid ' + color,
            borderRadius: '0 0 10px 10px', padding: '20px 24px', marginBottom: 32,
            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, color, fontSize: '0.95em' }}>
                Szczegóły — {versionLabel(v.model_version)}
              </span>
              <button onClick={() => handleVersionChange('all')}
                style={{ border: 'none', background: '#f0f0f0', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: '0.8em', color: '#555' }}>
                Zamknij
              </button>
            </div>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: '0.88em' }}>
              <div>
                <div style={{ color: '#aaa', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.78em', letterSpacing: '0.05em' }}>Predykcje</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span>Wygenerowane: <strong>{v.total_count ?? '—'}</strong></span>
                  <span>Ocenianie zakończone: <strong>{v.evaluated_count}</strong></span>
                  <span style={{ paddingLeft: 12, color: '#2e7d32' }}>↳ udane: <strong>{v.evaluated_count - (v.no_bidders_count ?? 0)}</strong></span>
                  <span style={{ paddingLeft: 12, color: '#aaa' }}>↳ nieudane: <strong>{v.no_bidders_count ?? 0}</strong></span>
                  <span style={{ color: '#888' }}>Czeka na ocenę: <strong>{pending}</strong></span>
                </div>
              </div>
              <div>
                <div style={{ color: '#aaa', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.78em', letterSpacing: '0.05em' }}>Wyniki</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span>Śr. precyzja: <strong style={{ color: '#2e7d32' }}>{pct(v.avg_precision)}</strong></span>
                  <span>Śr. pokrycie (recall@10): <strong style={{ color: '#1565c0' }}>{pct(v.avg_recall)}</strong></span>
                  <span>Śr. liczba oferentów: <strong>{v.avg_actual_bidders != null ? v.avg_actual_bidders.toFixed(1) : '—'}</strong></span>
                  <span>Łączne trafienia: <strong>{v.total_correct ?? '—'}</strong></span>
                </div>
              </div>
              <div>
                <div style={{ color: '#aaa', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.78em', letterSpacing: '0.05em' }}>Rozkład trafień (recall@10)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ color: '#1b5e20' }}>100%: <strong>{v.perfect_hits_count ?? '—'}</strong></span>
                  <span style={{ color: '#2e7d32' }}>75–99%: <strong>{v.hits_75_99_count ?? '—'}</strong></span>
                  <span style={{ color: '#558b2f' }}>50–74%: <strong>{v.hits_50_74_count ?? '—'}</strong></span>
                  <span style={{ color: '#f9a825' }}>25–49%: <strong>{v.hits_25_49_count ?? '—'}</strong></span>
                  <span style={{ color: '#e65100' }}>1–24%: <strong>{v.hits_1_24_count ?? '—'}</strong></span>
                  <span style={{ color: '#c62828' }}>0%: <strong>{v.zero_hits_count ?? '—'}</strong></span>
                </div>
              </div>
              <div>
                <div style={{ color: '#aaa', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.78em', letterSpacing: '0.05em' }}>Oceny nieudane</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ color: '#888' }}>Brak oferentów: <strong>{(v.no_bidders_count ?? 0) - (v.cancelled_count ?? 0)}</strong></span>
                  <span style={{ color: '#888' }}>Anulowany przetarg: <strong>{v.cancelled_count ?? '—'}</strong></span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Timeline chart ── */}
      <div style={{
        background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
        padding: '20px 24px', marginBottom: 32, boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1em', color: '#333' }}>
            Precyzja i pokrycie w czasie
            {selectedVersion !== 'all' && (
              <span style={{ marginLeft: 8, color: '#888', fontWeight: 400, fontSize: '0.9em' }}>
                — {versionLabel(selectedVersion)}
              </span>
            )}
          </h3>
          {selectedVersion !== 'all' && (
            <button
              onClick={() => handleVersionChange('all')}
              style={{ border: 'none', background: '#f5f5f5', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: '0.8em', color: '#555' }}
            >
              Pokaż wszystkie wersje
            </button>
          )}
        </div>

        <SVGLineChart
          data={chartData}
          avgPrecision={versionAvgPrecision}
          avgRecall={versionAvgRecall}
        />
      </div>

      {/* ── Individual runs table ── */}
      {(() => {
        const reversed = [...filteredTimeline].reverse();
        const totalPages = Math.ceil(reversed.length / PAGE_SIZE);
        const pageRows = reversed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        const btnStyle = (disabled) => ({
          border: '1px solid #ddd', background: disabled ? '#f9f9f9' : '#fff',
          borderRadius: 6, padding: '4px 12px', cursor: disabled ? 'default' : 'pointer',
          fontSize: '0.82em', color: disabled ? '#ccc' : '#555',
        });
        return (
          <div style={{
            background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
            padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: '1em', color: '#333' }}>
                Ocenione przetargi ({reversed.length})
              </h3>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => setPage(1)}>«</button>
                  <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                  <span style={{ fontSize: '0.82em', color: '#888' }}>
                    {page} / {totalPages}
                  </span>
                  <button style={btnStyle(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                  <button style={btnStyle(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                </div>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82em' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee' }}>
                    {['Data oceny', 'Wersja modelu', 'Przetarg', 'Faktyczni oferenci', 'Trafione', 'Precyzja', 'Pokrycie', 'Pominięci (nowi)'].map((h, i) => (
                      <th key={i} style={{ textAlign: i < 3 ? 'left' : 'center', padding: '6px 8px', color: '#888', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#888' }}>
                        {r.evaluated_at ? new Date(r.evaluated_at).toLocaleDateString('pl-PL') : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontSize: '0.9em', color: '#555' }}>
                        {versionLabel(r.model_version)}
                      </td>
                      <td style={{ padding: '6px 8px', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <a href={`/tenders/${r.tender_id}/show`} style={{ color: '#1565c0', textDecoration: 'none' }}>
                          {r.tender__title || `Przetarg #${r.tender_id}`}
                        </a>
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600 }}>
                        {r.actual_bidders_count ?? '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                        {r.correct_count ?? '—'}/{r.predicted_count ?? '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                        {r.precision != null ? (
                          <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 10, padding: '1px 8px', fontWeight: 700 }}>
                            {pct(r.precision)}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                        {r.recall != null ? (
                          <span style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: 10, padding: '1px 8px', fontWeight: 700 }}>
                            {pct(r.recall)}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px', color: r.excluded_new_bidders_count > 0 ? '#e65100' : '#aaa' }}>
                        {r.excluded_new_bidders_count ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
                <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                <span style={{ fontSize: '0.82em', color: '#888' }}>
                  Strona {page} z {totalPages} ({(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, reversed.length)} z {reversed.length})
                </span>
                <button style={btnStyle(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                <button style={btnStyle(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default ModelStats;
